import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Loader2, Pencil, AlertTriangle } from 'lucide-react';
import { CountryCombobox } from '@/components/insurance/CountryCombobox';
import type { ICompanyInfoForm } from '@/hooks/useCompanySettingsForm';

interface ICompanyInfoSectionProps {
  form: ICompanyInfoForm;
  isSubmitting: boolean;
  onSetField: <K extends keyof ICompanyInfoForm>(key: K, value: ICompanyInfoForm[K]) => void;
  onSave: () => void;
}

export function CompanyInfoSection({
  form,
  isSubmitting,
  onSetField,
  onSave,
}: ICompanyInfoSectionProps) {
  const [isEmailEditing, setIsEmailEditing] = useState(false);

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <div className="rounded-lg bg-primary/10 p-2">
            <Building2 className="w-4 h-4 text-foreground" />
          </div>
          Company Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company-name">Company Name</Label>
            <Input
              id="company-name"
              value={form.name}
              onChange={e => onSetField('name', e.target.value)}
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company-email">Admin Email</Label>
            <div className="flex gap-2">
              <Input
                id="company-email"
                type="email"
                value={form.email}
                onChange={e => onSetField('email', e.target.value)}
                disabled={isSubmitting || !isEmailEditing}
                className={!isEmailEditing ? 'bg-muted' : ''}
              />
              {!isEmailEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => setIsEmailEditing(true)}
                  title="Edit email"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
            </div>
            {isEmailEditing && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2.5 dark:bg-amber-950">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Changing the admin email will redirect all future communications and
                  notifications to the new address.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company-phone">Phone</Label>
            <Input
              id="company-phone"
              value={form.phoneNumber}
              onChange={e => onSetField('phoneNumber', e.target.value)}
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company-address">Address</Label>
            <Input
              id="company-address"
              value={form.address}
              onChange={e => onSetField('address', e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company-website">Website</Label>
            <Input
              id="company-website"
              value={form.website}
              onChange={e => onSetField('website', e.target.value)}
              placeholder="https://example.com"
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label>Country</Label>
            <CountryCombobox
              value={form.country}
              onChange={val => onSetField('country', val)}
              placeholder="Select country"
              disabled={isSubmitting}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={onSave} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
